import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { SnUsersAPI } from '@lib/api/services';
import { SnCurrentUser, SnSessionService } from '@lib/auth';
import { SnFormFieldConfig, ISnHasForm } from '@lib/form';
import { t, TranslationService } from '@lib/translation';
import { SnUserFieldsGeneratorService } from '@lib/users';

interface IFormModel {
  email: string;
  password: string;
}

@Component({
  selector: 'sn-change-email-dialog',
  templateUrl: './change-email.dialog.html',
  styleUrls: ['./change-email.dialog.scss'],
})
export class SnChangeEmailDialogComponent implements OnDestroy, OnInit, ISnHasForm<IFormModel> {
  public readonly form = new UntypedFormGroup({});
  public formFields: SnFormFieldConfig[] = [];

  public formModel: IFormModel = {
    email: this._currentUser.email,
    password: '',
  };

  public isLoading = false;
  public errorMessage: string;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _sessionService: SnSessionService,
    private readonly _usersAPI: SnUsersAPI,
    private readonly _matDialogRef: MatDialogRef<SnChangeEmailDialogComponent>,
    private readonly _currentUser: SnCurrentUser,
    private readonly _userFieldsGeneratorService: SnUserFieldsGeneratorService,
    private readonly _translationService: TranslationService,
  ) {}

  public ngOnInit(): void {
    if (this._translationService.getActiveLang()) {
      this._createFormFields();
    }
  }

  private _createFormFields(): void {
    this.formFields = [
      {
        wrappers: ['form-field-group'],
        fieldGroup: [
          this._userFieldsGeneratorService.getCurrentPasswordField({
            templateOptions: {
              required: true,
              attributes: {
                autocomplete: 'current-password',
              },
            },
          }),
          this._getChangeEmailField(),
        ],
      },
    ];
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public submitForm(): void {
    this.errorMessage = null;

    if (this.form.invalid) {
      return;
    }

    if (this._currentUser.email === this.formModel.email) {
      this._matDialogRef.close();
      return;
    }

    this.isLoading = true;

    const requestBody = {
      email: this.formModel.email,
      password: this.formModel.password,
    };

    this._currentUser
      .updateCurrentUser(requestBody)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe(
        (user) => {
          this._matDialogRef.close(user);
          this._sessionService.logout({
            reason: 'changeEmailDialog:emailChanged',
          });
        },
        (error) => {
          if (error?.status === 403) {
            this.errorMessage = 'Invalid password. Please try again.';
            this.form.get('password').setErrors({ invalid: true });
          } else {
            this.errorMessage = error?.error?.detail || 'An error occurred. Please try again.';
          }
        },
      );
  }

  public cancel(): void {
    this._matDialogRef.close();
  }

  private _getChangeEmailField(): SnFormFieldConfig {
    const baseField = this._userFieldsGeneratorService.getEmailField({
      asyncValidators: {
        validation: [this._usersAPI.validateUniqueEmail(this._currentUser.email)],
      },
    });

    baseField.templateOptions = this._translationService.createTranslatedFieldOptions(
      'common.email',
      'common.enterEmail',
      {
        appearance: 'fill',
        type: 'email',
        required: true,
      }
    );

    baseField.validation = {
      ...baseField.validation,
      messages: {
        ...baseField.validation?.messages,
      },
    };

    return baseField;
  }
}
